import React, {useEffect, useRef, useState} from "react";
import {useAppSelector} from "../../../hooks";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styled from "styled-components";
import {selectActiveTable, selectProjectSchema} from "../../../features/projectSlice";
import {faSearchengin} from "@fortawesome/free-brands-svg-icons";
import useDisableScroll from "../../hooks/useDisableScroll";
import {useKey} from "../../hooks/useKey";
import {Field, PrimitiveObject, SearchRef} from "../../../Types";
import SearchRefEntry from "./SearchRefEntry";
import SearchRefExtended from "./SearchRefExtended";

const MIN_BOTTOM_GAP = 420; // px

const MAX_MODAL_HEIGHT = 400;

const MIN_MODAL_WIDTH = 620;

interface PropsI {
    fieldSchema: Field & { searchRef: SearchRef },
    record: PrimitiveObject
}

export default function SearchRefModal({fieldSchema, record}: PropsI) {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrollLocked, setIsScrollLocked] = useState(false);
    const [extendedIndex, setExtendedIndex] = useState(-1)

    const viewportWidth = useAppSelector(state => state.layout.viewportWidth);
    const project = useAppSelector(selectProjectSchema);
    const currentTable = useAppSelector(selectActiveTable);
    const mobile = viewportWidth <= MIN_MODAL_WIDTH

    const btnRef = useRef<HTMLButtonElement | null>(null)
    useDisableScroll(isScrollLocked)

    const [btnOffsets, setBtnOffsets] = useState<{
        top: number;
        left: number;
        right: number;
        bottom: number;
        width: number;
        height: number;
    } | null>(null);

    useEffect(() => {
        if (!btnRef.current) return;

        if (!isOpen) {
            setIsScrollLocked(false)
            return;
        }

        const rect = btnRef.current.getBoundingClientRect();
        const bottomViewport = window.innerHeight - rect.bottom
        let top = rect.top
        let right = window.innerWidth - rect.right
        let left = rect.left
        let bottom = window.innerHeight - rect.bottom
        if (bottomViewport < MIN_BOTTOM_GAP) {
            const scrollDelta = MIN_BOTTOM_GAP - bottomViewport;
            top -= scrollDelta
            bottom += scrollDelta
            window.scrollBy({
                top: scrollDelta,
                behavior: "auto",
            });
        }
        if (right < MIN_MODAL_WIDTH && !mobile) {
            left -= MIN_MODAL_WIDTH - right;
        }
        setBtnOffsets({
            top: top,
            left: left,
            right: right,
            bottom: bottom,
            width: rect.width,
            height: rect.height,
        });
        setIsScrollLocked(true)

    }, [isOpen, mobile]);

    useKey('Escape', () => close(),
        {priority: 100, suppressBelow: true, enabled: isOpen});

    const close = () => setIsOpen(false)
    const setExtended = (index: number) => setExtendedIndex(index)

    return <Wrapper>
        <button className={'btn-icon'} onClick={() => setIsOpen(true)} ref={btnRef}>
            <FontAwesomeIcon icon={faSearchengin} className={'fa-color'} title={"Шукати"}
                             style={{width: "1.2rem", height: "1.2rem", rotate: "y 180deg"}}/>
        </button>
        {isOpen && <OverlayContainer onClick={close}>
            {isScrollLocked && btnOffsets && <Container $top={btnOffsets.top} $left={btnOffsets.left}
                                                        $maxHeight={btnOffsets.bottom} $mobile={mobile}
                                                        onClick={e => e.stopPropagation()}>
                <div className="entries">
                    {fieldSchema.searchRef.map((s, i) => {
                        const masked = i !== extendedIndex && extendedIndex > -1;
                        if (mobile && masked)
                            return null
                        return <SearchRefEntry currentTable={currentTable} table={s.table}
                                               condition={s.condition}
                                               currentSchema={project.tables[currentTable]}
                                               schema={project.tables[s.table]}
                                               closeSearch={close}
                                               index={i}
                                               setExtended={setExtended}
                                               isMasked={masked}
                                               isExtended={i === extendedIndex}
                                               record={record} key={i}/>
                    })}
                </div>
                {extendedIndex > -1 && <div className="extended">
                    <SearchRefExtended currentTable={currentTable} table={fieldSchema.searchRef[extendedIndex].table}
                                       condition={fieldSchema.searchRef[extendedIndex].condition}
                                       currentSchema={project.tables[currentTable]}
                                       schema={project.tables[fieldSchema.searchRef[extendedIndex].table]}
                                       record={record} closeSearch={close}/>
                </div>}
            </Container>}
        </OverlayContainer>}
    </Wrapper>
}

const Wrapper = styled.div`
    position: relative;
`

const Container = styled.div<{ $top: number, $left: number, $maxHeight: number, $mobile: boolean }>`


    display: flex;
    flex-flow: ${props => props.$mobile ? 'column' : 'row'};
    position: absolute;
    top: ${props => props.$top}px;
    left: ${props => props.$left}px;
    max-height: ${props => props.$maxHeight}px;

    overflow-y: auto;
    
    overflow-x: hidden;

    max-width: ${props => props.$mobile ? "300" : "600"}px;
    min-width: ${props => props.$mobile ? "200" : "400"}px;
    width: 100%;

    & .entries {

        //border-radius: .2rem;
        
        display: flex;
        flex-flow: column;
        gap: .2rem;
        //flex: 1 0 100%;
    }

    & .extended {
        //flex: 1 0 100%;
        //flex-grow: 1;
        //width: 100%;
    }
`

export const OverlayContainer = styled.div<{ $left?: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: ${props => props.$left ? 'start' : 'center'};
    z-index: 9999;
`;
